/**
 * ProposalDevelopmentPage - Control Center for Configuration Analysis
 *
 * RESPONSIBILITY:
 * - Orchestrates UI for configuration management
 * - Delegates business logic to specialized hooks
 * - Displays configurations and candidates
 *
 * ARCHITECTURE:
 * - useConfigurationSelection: Manages selection state and auto-selection
 * - useConfigurationWorkflow: Handles bond add/update/delete workflows
 * - useAutoRehydration: Rebuilds missing data and auto-processes configs
 */

import { Box, Button, Heading, Spinner, Text, VStack } from '@chakra-ui/react';
import { ArrowLeft } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { DrawerState } from '~/components/layout/page-utilities/BottomDrawer';
import { PageLayoutGrid } from '~/components/layout/page-utilities/PageLayoutGrid';
import { useAppHeader } from '~/components/layout/shared/AppHeaderContext';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { CashflowValidationModal } from '~/components/modals/CashflowValidationModal';
import { ConfirmModal } from '~/components/ui/ConfirmModal';
import { toaster } from '~/components/ui/toaster';
import { useProjectStore } from '~/hooks/useProjectStore';
import { useTestingStore } from '~/hooks/useTestingStore';
import { PROJECT_ROUTES } from '~/pages/project/projectRoutes';
import { type CashflowValidationResult, validateCashflows } from '~/services/cashflow-validation';
import { useAppStore } from '~/store';
import type { BondConfiguration } from '~/types/bond-inputs';
import type { MillageOverrides, TaxableValueOverrides } from '~/types/overrides';
import { formatMarkdownTable } from '~/utils/markdown-table-formatter';
import { AddBondModal, type QuickBondInput } from './AddBondModal';
import { BondConfigurationModal } from './BondConfigurationModal';
import { BondsSidebar } from './BondsSidebar';
import { CandidateDataDrawer } from './CandidateDataDrawer';
import { CandidateHeader } from './CandidateHeader';
import { CandidatesSidebar } from './CandidatesSidebar';
import { CandidateGraphView } from './components/CandidateGraphView';
import { ConfigurationCardsContainer } from './components/ConfigurationCardsContainer';
import { ReadinessChecklist } from './components/ReadinessChecklist';
import { useAutoRehydration, useConfigurationSelection, useConfigurationWorkflow } from './hooks';

const ProposalDevelopmentPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeader } = useAppHeader();

  // Check if we navigated from the debug page
  const fromDebug = (location.state as { fromDebug?: boolean })?.fromDebug ?? false;

  // Get project data from store
  const project = useProjectStore((s) => (projectId ? s.projects[projectId] : undefined));
  const configurations = useProjectStore((s) =>
    projectId ? s.projects[projectId]?.configurations || null : null
  );

  // User info for super admin features
  const userInfo = useAppStore((s) => s.userInfo);
  const isSuperAdmin = userInfo?.isSuperAdmin ?? false;
  const isTestingProject = project?.metadata?.isTestingProject ?? false;

  // Testing store for values testing navigation
  const loadTestingData = useTestingStore((s) => s.loadTestingData);

  // ===========================================
  // Selection State (extracted hook)
  // ===========================================
  const {
    selectedConfigurationId,
    selectedCandidateId,
    selectedBondId,
    setSelectedConfigurationId,
    setSelectedCandidateId,
    setSelectedBondId,
    currentConfiguration,
    candidates,
    selectedCandidate,
    bonds,
    handleConfigurationSelect,
    handleCandidateSelect,
  } = useConfigurationSelection({ configurations });

  // NOTE: Configuration ID transitions (client → server ID) are now handled atomically
  // by useConfigurationIdTransition hook inside useConfigurationSelection

  // ===========================================
  // Auto-Rehydration (extracted hook) - Lazy loading
  // ===========================================
  const { processConfigurationOnDemand, configNeedsProcessing } = useAutoRehydration({
    projectId,
    project,
    configurations,
    selectedConfigurationId,
    onCandidateSelected: setSelectedCandidateId,
  });

  // ===========================================
  // Local UI State
  // ===========================================
  const [drawerState, setDrawerState] = useState<DrawerState>('collapsed');
  const [isAddBondModalOpen, setIsAddBondModalOpen] = useState(false);
  const [isBondDetailsModalOpen, setIsBondDetailsModalOpen] = useState(false);
  const [quickBondDataForDetails, setQuickBondDataForDetails] = useState<QuickBondInput | null>(
    null
  );
  const [isProcessingConfig, setIsProcessingConfig] = useState(false);

  // Config deletion state
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);
  const [configDeleteWarning, setConfigDeleteWarning] = useState<string | null>(null);
  const [isConfigDeleteModalOpen, setIsConfigDeleteModalOpen] = useState(false);

  // Sanity check modal state
  const [isSanityCheckModalOpen, setIsSanityCheckModalOpen] = useState(false);
  const [sanityCheckResult, setSanityCheckResult] = useState<CashflowValidationResult | null>(null);

  // Favorites state - use stable empty array to avoid infinite loops (see react-dependency-patterns.md)
  const [showFavoritesView, setShowFavoritesView] = useState(false);
  const favoritesFromStore = useProjectStore((s) =>
    projectId ? s.projects[projectId]?.favorites : null
  );
  const favorites = useMemo(() => favoritesFromStore ?? [], [favoritesFromStore]);
  const addFavorite = useProjectStore((s) => s.addFavorite);
  const removeFavorite = useProjectStore((s) => s.removeFavorite);
  const isFavorited = useProjectStore((s) => s.isFavorited);

  // ===========================================
  // Configuration Workflow (extracted hook)
  // ===========================================
  const workflow = useConfigurationWorkflow({
    projectId: projectId || '',
    project: project!,
    configurations,
    selectedConfigurationId,
    onConfigurationCreated: setSelectedConfigurationId,
    onCandidateSelected: setSelectedCandidateId,
    onModalClose: () => {
      setIsAddBondModalOpen(false);
      setIsBondDetailsModalOpen(false);
      setQuickBondDataForDetails(null);
      setSelectedBondId(null);
    },
  });

  // ===========================================
  // Header Configuration
  // ===========================================
  const projectTitle = project?.metadata?.name || 'Proposal Development';

  const handleBack = useCallback(() => {
    navigate(`/projects/${projectId}/${PROJECT_ROUTES.EXISTING_DEBT}`);
  }, [navigate, projectId]);

  const handleValuesTestingClick = useCallback(() => {
    if (!projectId || !currentConfiguration) {
      toaster.create({
        title: 'Cannot open Values Testing',
        description: 'No configuration available.',
        type: 'warning',
      });
      return;
    }

    // Combine viable and failed candidates for full comparison
    const viableCandidates = currentConfiguration.candidates || [];
    const failedCandidates = currentConfiguration.failedCandidates || [];
    const allCandidates = [...viableCandidates, ...failedCandidates];

    if (allCandidates.length === 0) {
      toaster.create({
        title: 'Cannot open Values Testing',
        description: 'No candidates available for the current configuration.',
        type: 'warning',
      });
      return;
    }

    loadTestingData(
      projectId,
      project?.metadata?.name || 'Unknown Project',
      currentConfiguration.id,
      currentConfiguration.name || `Config ${currentConfiguration.orderId || 1}`,
      allCandidates
    );
    navigate(`/values-testing/${projectId}/${currentConfiguration.id}`);
  }, [projectId, currentConfiguration, project?.metadata?.name, loadTestingData, navigate]);

  const handleCopyAsTestingProject = useCallback(async () => {
    if (!projectId) return;

    if (isTestingProject) {
      toaster.create({
        title: 'Already a Testing Project',
        description: 'This project is already a testing project.',
        type: 'info',
      });
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/projects/${projectId}/copy-as-testing`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to copy project');

      const data = await response.json();
      toaster.create({
        title: 'Testing Project Created',
        description: `Created "${data.project.name}" with ${Object.keys(data.configurationIdMap).length} configurations.`,
        type: 'success',
        duration: 8000,
      });
      navigate(`/projects/${data.project.id}/proposal`);
    } catch (error) {
      console.error('Failed to copy project as testing:', error);
      toaster.create({
        title: 'Error',
        description: 'Failed to create testing project.',
        type: 'error',
      });
    }
  }, [projectId, navigate, isTestingProject]);

  const handleDebugClick = useCallback(() => {
    if (!projectId || !selectedConfigurationId) {
      toaster.create({
        title: 'Cannot open Debug Inspector',
        description: 'Please select a configuration first.',
        type: 'warning',
      });
      return;
    }
    navigate(`/projects/${projectId}/debug/${selectedConfigurationId}`);
  }, [projectId, selectedConfigurationId, navigate]);

  // ===========================================
  // Copy Cashflow Information Handler
  // ===========================================
  const handleCopyCashflowInfo = useCallback(async () => {
    if (!selectedCandidate || !project || !currentConfiguration) {
      toaster.create({
        title: 'Cannot Copy Cashflow Information',
        description: 'Please select a candidate first.',
        type: 'warning',
      });
      return;
    }

    try {
      // Get shape ID from candidate
      const shapeId =
        selectedCandidate.millageInformation?.millageShape?.shapeId || 'Unknown Shape';

      // Get project and client info
      const projectName = project.metadata?.name || 'Unknown Project';
      const clientName = project.metadata?.clientName || 'Unknown Client';

      // Get number of bonds
      const bondCount = currentConfiguration.bondSeries?.length || 0;

      // Format cashflow header
      const headerFields = [
        'Payment Date',
        'Tax Year',
        'Fiscal Year End',
        'Millage Rate',
        'Total Revenue (Tax Year)',
        'Payment Revenue %',
        'Payment Revenue',
        'DRF Starting Balance',
        'Debt Payment Amount',
        'Existing Debt Principal',
        'Existing Debt Interest',
        'Revenue - Existing Debt',
        'Is Interest Only',
        'Interest Obligation',
        'Available for Bonds',
        'Total Bond Payment',
        'DRF Adjustment',
        'DRF Ending Balance',
        'DRF Net Change',
        'DRF Net Revenue',
      ];

      // Format bond payment columns dynamically - use bond series order
      const bondSeries = currentConfiguration.bondSeries || [];
      const bondHeaders: string[] = [];
      for (const bond of bondSeries) {
        const bondName = bond.name || `Bond ${bond.id || ''}`;
        bondHeaders.push(
          `${bondName} - Principal`,
          `${bondName} - Interest`,
          `${bondName} - Total`
        );
      }

      const allHeaders = [...headerFields, ...bondHeaders];

      // Format cashflow records
      const sortedCashFlows = [...(selectedCandidate.cashFlows || [])].sort(
        (a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()
      );

      const formatValue = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') {
          // Format currency values with commas
          if (Math.abs(value) >= 1000) {
            return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
          return value.toFixed(2);
        }
        return String(value);
      };

      const rows = sortedCashFlows.map((cf) => {
        const baseRow: string[] = [
          cf.paymentDate,
          String(cf.relatedTaxBillYear),
          cf.relatedFiscalYearEndingDate,
          formatValue(cf.millageRate),
          formatValue(cf.totalRevenueForTaxYear),
          formatValue(`${(cf.paymentRevenuePercentage * 100).toFixed(1)}%`),
          formatValue(cf.paymentRevenuePortion),
          formatValue(cf.drfStartingBalance),
          formatValue(cf.debtPaymentAmount),
          formatValue(cf.existingDebtPrincipal),
          formatValue(cf.existingDebtInterest),
          formatValue(cf.revenueMinusExistingDebt),
          formatValue(cf.isInterestOnly),
          formatValue(cf.interestObligation),
          formatValue(cf.availableForBonds),
          formatValue(cf.totalBondPayment),
          formatValue(cf.drfAdjustment),
          formatValue(cf.drfEndingBalance),
          formatValue(cf.drfNetChange),
          formatValue(cf.drfNetRevenue),
        ];

        // Add bond payment columns - match by bondSeriesId
        const bondRow: string[] = [];
        for (const bond of bondSeries) {
          const bondPayment = cf.bondPayments?.find((bp) => bp.bondSeriesId === bond.id);
          if (bondPayment) {
            bondRow.push(
              formatValue(bondPayment.principal),
              formatValue(bondPayment.totalInterest),
              formatValue(bondPayment.totalPayment)
            );
          } else {
            bondRow.push('', '', '');
          }
        }

        return [...baseRow, ...bondRow];
      });

      // Format as markdown table
      const markdownTable = formatMarkdownTable(allHeaders, rows, {
        align: 'auto', // Auto-detect numeric columns for right alignment
        minColumnWidth: 3,
        maxColumnWidth: 50,
      });

      // Build the formatted text
      const textLines = [
        '='.repeat(80),
        'CASHFLOW INFORMATION',
        '='.repeat(80),
        '',
        `Client: ${clientName}`,
        `Project: ${projectName}`,
        `Shape ID: ${shapeId}`,
        `Configuration: ${currentConfiguration.name || `Config ${currentConfiguration.orderId || 1}`}`,
        `Number of Bonds: ${bondCount}`,
        '',
        '```markdown',
        markdownTable,
        '```',
        '',
        `Total Records: ${sortedCashFlows.length}`,
        `Date Range: ${sortedCashFlows[0]?.paymentDate || 'N/A'} to ${
          sortedCashFlows[sortedCashFlows.length - 1]?.paymentDate || 'N/A'
        }`,
      ];

      const formattedText = textLines.join('\n');

      // Copy to clipboard
      await navigator.clipboard.writeText(formattedText);

      toaster.create({
        title: 'Cashflow Information Copied',
        description: 'Cashflow data has been copied to your clipboard.',
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to copy cashflow information:', error);
      toaster.create({
        title: 'Copy Failed',
        description:
          error instanceof Error ? error.message : 'Failed to copy cashflow information.',
        type: 'error',
      });
    }
  }, [selectedCandidate, project, currentConfiguration]);

  // ===========================================
  // Sanity Check Cashflows Handler
  // ===========================================
  const handleSanityCheckCashflows = useCallback(() => {
    if (!selectedCandidate) {
      toaster.create({
        title: 'Cannot Run Sanity Check',
        description: 'Please select a candidate first.',
        type: 'warning',
      });
      return;
    }

    const cashFlows = selectedCandidate.cashFlows || [];
    if (cashFlows.length === 0) {
      toaster.create({
        title: 'No Cashflows',
        description: 'The selected candidate has no cashflow data.',
        type: 'warning',
      });
      return;
    }

    // Run validation
    const result = validateCashflows(cashFlows);
    setSanityCheckResult(result);
    setIsSanityCheckModalOpen(true);
  }, [selectedCandidate]);

  // ===========================================
  // Dev Utilities Event Listeners
  // ===========================================
  useEffect(() => {
    const handleCopyAsTestingEvent = () => handleCopyAsTestingProject();
    const handleValuesTestingEvent = () => handleValuesTestingClick();
    const handleDebugInspectorEvent = () => handleDebugClick();
    const handleCopyCashflowInfoEvent = () => handleCopyCashflowInfo();
    const handleSanityCheckEvent = () => handleSanityCheckCashflows();

    window.addEventListener('dev-utilities:copy-as-testing', handleCopyAsTestingEvent);
    window.addEventListener('dev-utilities:values-testing', handleValuesTestingEvent);
    window.addEventListener('dev-utilities:debug-inspector', handleDebugInspectorEvent);
    window.addEventListener('dev-utilities:copy-cashflow-info', handleCopyCashflowInfoEvent);
    window.addEventListener('dev-utilities:sanity-check-cashflows', handleSanityCheckEvent);

    return () => {
      window.removeEventListener('dev-utilities:copy-as-testing', handleCopyAsTestingEvent);
      window.removeEventListener('dev-utilities:values-testing', handleValuesTestingEvent);
      window.removeEventListener('dev-utilities:debug-inspector', handleDebugInspectorEvent);
      window.removeEventListener('dev-utilities:copy-cashflow-info', handleCopyCashflowInfoEvent);
      window.removeEventListener('dev-utilities:sanity-check-cashflows', handleSanityCheckEvent);
    };
  }, [
    handleCopyAsTestingProject,
    handleValuesTestingClick,
    handleDebugClick,
    handleCopyCashflowInfo,
    handleSanityCheckCashflows,
  ]);

  // Broadcast candidate selection state for DevUtilitiesMenu
  useEffect(() => {
    const event = new CustomEvent('dev-utilities:candidate-selection-changed', {
      detail: { hasSelectedCandidate: !!selectedCandidate },
    });
    window.dispatchEvent(event);
  }, [selectedCandidate]);

  // Respond to requests for current candidate state (for DevUtilitiesMenu initialization)
  useEffect(() => {
    const handleRequestState = () => {
      const event = new CustomEvent('dev-utilities:candidate-selection-changed', {
        detail: { hasSelectedCandidate: !!selectedCandidate },
      });
      window.dispatchEvent(event);
    };

    window.addEventListener('dev-utilities:request-candidate-state', handleRequestState);

    return () => {
      window.removeEventListener('dev-utilities:request-candidate-state', handleRequestState);
    };
  }, [selectedCandidate]);

  // Track previous values to prevent unnecessary header updates
  const prevTitleRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevTitleRef.current === projectTitle && prevTitleRef.current !== undefined) {
      return;
    }

    prevTitleRef.current = projectTitle;

    setHeader({
      title: projectTitle,
      leftActions: fromDebug ? undefined : (
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Existing Debt
        </Button>
      ),
    });
  }, [setHeader, projectTitle, handleBack, fromDebug]);

  // ===========================================
  // Handlers
  // ===========================================
  const handleBondSelect = (id: string) => {
    const bond = currentConfiguration?.bondSeries.find((b) => b.id === id);
    if (!bond) return;

    const dateRecords = project?.dateRecords || project?.dateElements?.dateRecords || [];
    const firstPrincipalDate = new Date(bond.firstPrincipalPaymentDate);

    const matchingRecord = dateRecords.find((dr) => {
      if (!dr.paymentDates || dr.paymentDates.length === 0) return false;
      return (
        dr.paymentDates.some((pd) => {
          const pdDate = new Date(pd);
          return (
            pdDate.getFullYear() === firstPrincipalDate.getFullYear() &&
            pdDate.getMonth() === firstPrincipalDate.getMonth() &&
            pdDate.getDate() === firstPrincipalDate.getDate()
          );
        }) || dr.lastPaymentDateOfFiscalYear === bond.firstPrincipalPaymentDate
      );
    });

    const startDate = new Date(bond.firstPrincipalPaymentDate);
    const endDate = new Date(bond.finalPrincipalPaymentDate);
    const yearsDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    const durationYears = Math.round(yearsDiff) + 1;
    const taxYear = matchingRecord?.taxYear || firstPrincipalDate.getFullYear();

    setQuickBondDataForDetails({
      startingTaxYear: taxYear,
      term: durationYears,
      targetAmount: bond.targetInformation?.principalTargetAmount,
    });
    setIsBondDetailsModalOpen(true);
    setSelectedBondId(id);
  };

  const handleConfigurationDeleteRequest = (configId: string) => {
    if (!projectId || !configurations) return;

    const config = configurations.find((c) => c.id === configId);
    if (!config) return;

    // Check for favorites in this configuration
    const favorites = useProjectStore.getState().getFavoritesForConfig(projectId, configId);

    setConfigToDelete(configId);
    if (favorites.length > 0) {
      setConfigDeleteWarning(
        `This will also remove ${favorites.length} favorited candidate${favorites.length > 1 ? 's' : ''}.`
      );
    } else {
      setConfigDeleteWarning(null);
    }
    setIsConfigDeleteModalOpen(true);
  };

  const handleConfigurationDeleteConfirm = () => {
    if (!projectId || !configurations || !configToDelete) return;

    const config = configurations.find((c) => c.id === configToDelete);
    if (!config) return;

    useProjectStore.getState().removeConfiguration(projectId, configToDelete);

    if (selectedConfigurationId === configToDelete) {
      const remainingConfigs = configurations.filter((c) => c.id !== configToDelete);

      if (remainingConfigs.length > 0) {
        // Sort by createdAt (newest first) to select the most recent one
        const sortedConfigs = [...remainingConfigs].sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // Descending - newest first
        });
        setSelectedConfigurationId(sortedConfigs[0].id);
      } else {
        setSelectedConfigurationId(null);
        setSelectedCandidateId(null);
      }
    }

    toaster.create({
      title: 'Configuration deleted',
      description: `Configuration ${config.orderId} has been removed`,
      type: 'success',
      duration: 3000,
    });

    setConfigToDelete(null);
    setConfigDeleteWarning(null);
    setIsConfigDeleteModalOpen(false);
  };

  const handleConfigurationNameEdit = (configId: string, newName: string) => {
    if (!projectId || !configurations) return;
    const config = configurations.find((c) => c.id === configId);
    if (!config) return;

    useProjectStore.getState().updateConfiguration(projectId, { ...config, name: newName });
  };

  const handleQuickAddBond = (quickData: QuickBondInput) => {
    workflow.addBondQuick(quickData);
  };

  const handleOpenBondDetails = (quickData: QuickBondInput) => {
    setQuickBondDataForDetails(quickData);
    setIsAddBondModalOpen(false);
    setIsBondDetailsModalOpen(true);
  };

  const handleAddBond = (config: BondConfiguration) => {
    if (selectedBondId && currentConfiguration) {
      workflow.updateBond(selectedBondId, config);
    } else {
      workflow.addBondFull(config);
    }
  };

  // Wrapped configuration select with lazy loading and loading state
  const handleConfigurationSelectWithProcessing = useCallback(
    async (configId: string, targetShapeId?: string) => {
      const needsProcessing = configNeedsProcessing(configId);

      if (needsProcessing) {
        // Show loading state with minimum 300ms display time
        setIsProcessingConfig(true);

        // Yield to event loop so React can render the spinner before heavy processing
        await new Promise((resolve) => setTimeout(resolve, 10));

        const startTime = Date.now();

        // Process the configuration (synchronous heavy work)
        processConfigurationOnDemand(configId);

        // Ensure minimum 300ms loading display
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 300 - elapsed);
        if (remainingDelay > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingDelay));
        }

        // Select the configuration while still showing spinner
        handleConfigurationSelect(configId, targetShapeId);

        // Extra delay to let candidate selection and chart settle before hiding spinner
        await new Promise((resolve) => setTimeout(resolve, 100));

        setIsProcessingConfig(false);
      } else {
        // No processing needed, just select immediately
        handleConfigurationSelect(configId, targetShapeId);
      }
    },
    [configNeedsProcessing, processConfigurationOnDemand, handleConfigurationSelect]
  );

  // Favorites handlers
  const handleToggleFavorite = useCallback(
    async (shapeId: string) => {
      if (!projectId || !selectedConfigurationId) return;

      const isCurrentlyFavorited = isFavorited(projectId, selectedConfigurationId, shapeId);

      if (isCurrentlyFavorited) {
        await removeFavorite(projectId, selectedConfigurationId, shapeId);
      } else {
        await addFavorite(projectId, selectedConfigurationId, shapeId);
      }
    },
    [projectId, selectedConfigurationId, isFavorited, addFavorite, removeFavorite]
  );

  const handleFavoriteSelect = useCallback(
    (configId: string, shapeId: string) => {
      // Close favorites view and navigate to the configuration + candidate
      setShowFavoritesView(false);
      handleConfigurationSelectWithProcessing(configId, shapeId);
    },
    [handleConfigurationSelectWithProcessing]
  );

  const handleToggleFavoritesView = useCallback(() => {
    setShowFavoritesView((prev) => !prev);
  }, []);

  // Handler for taxable value customization from drawer
  const handleCustomizeTaxableValues = useCallback(
    async (overrides: TaxableValueOverrides) => {
      await workflow.createWithTaxableValueOverrides(overrides);
    },
    [workflow]
  );

  // Handler for millage rate customization from drawer
  const handleCustomizeMillageRates = useCallback(
    async (overrides: MillageOverrides) => {
      await workflow.createWithMillageOverrides(overrides);
    },
    [workflow]
  );

  // ===========================================
  // Early Returns
  // ===========================================
  if (!projectId) {
    navigate('/');
    return null;
  }

  if (!project) {
    return (
      <Box p={8}>
        <Text color="red.500">Project not found</Text>
      </Box>
    );
  }

  // ===========================================
  // Readiness Checks
  // ===========================================
  const hasProjectDates = !!(project.dateRecords || project.dateElements?.dateRecords);
  const hasTaxableValue = !!(
    project.taxableValueMap && Object.keys(project.taxableValueMap).length > 0
  );
  const hasMillageInputs = !!project.millageInputs?.peakMillage;
  const hasDRFInputs = !!project.drfInputs?.startingBalance;
  const hasProjectTargets = hasMillageInputs && hasDRFInputs;
  const isReadyForBond = hasProjectDates && hasTaxableValue && hasProjectTargets;

  // ===========================================
  // Bond Configuration Modal Initial Config
  // ===========================================
  const getBondModalInitialConfig = () => {
    if (!quickBondDataForDetails) return undefined;

    const selectedBond = selectedBondId
      ? currentConfiguration?.bondSeries.find((b) => b.id === selectedBondId)
      : null;

    if (selectedBond) {
      const dateRecords = project.dateRecords || project.dateElements?.dateRecords || [];
      const firstPrincipalDate = new Date(selectedBond.firstPrincipalPaymentDate);
      const matchingRecord = dateRecords.find((dr) => {
        if (!dr.paymentDates || dr.paymentDates.length === 0) return false;
        return (
          dr.paymentDates.some((pd) => {
            const pdDate = new Date(pd);
            return (
              pdDate.getFullYear() === firstPrincipalDate.getFullYear() &&
              pdDate.getMonth() === firstPrincipalDate.getMonth() &&
              pdDate.getDate() === firstPrincipalDate.getDate()
            );
          }) || dr.lastPaymentDateOfFiscalYear === selectedBond.firstPrincipalPaymentDate
        );
      });
      const taxYear = matchingRecord?.taxYear || firstPrincipalDate.getFullYear();

      // biome-ignore lint/suspicious/noExplicitAny: Bond may have internal __interestRateEscalation field
      const bondWithEscalation = selectedBond as any;
      const interestRateSettings = bondWithEscalation.__interestRateEscalation
        ? {
            startRate: bondWithEscalation.__interestRateEscalation.startRate * 100,
            incrementRate: bondWithEscalation.__interestRateEscalation.incrementRate * 100,
            maxRate: bondWithEscalation.__interestRateEscalation.maxRate * 100,
          }
        : undefined;

      return {
        name: selectedBond.name,
        issueDate: selectedBond.issueDate,
        taxYear,
        durationYears: quickBondDataForDetails.term,
        targetPrincipal: selectedBond.targetInformation?.principalTargetAmount,
        interestRateSettings,
      };
    }

    // New bond from quick input
    const dateRecords = project.dateRecords || project.dateElements?.dateRecords || [];
    const startingYearRecord = dateRecords.find(
      (dr) => dr.taxYear === quickBondDataForDetails.startingTaxYear
    );
    const previousTaxYear = quickBondDataForDetails.startingTaxYear - 1;
    const previousYearRecord = dateRecords.find((dr) => dr.taxYear === previousTaxYear);

    let issueDate: string;
    if (previousYearRecord?.fiscalYearEndDate) {
      issueDate = previousYearRecord.fiscalYearEndDate;
    } else if (startingYearRecord?.fiscalYearEndDate) {
      const fiscalYearEndDate = new Date(startingYearRecord.fiscalYearEndDate);
      const issueDateObj = new Date(fiscalYearEndDate);
      issueDateObj.setFullYear(fiscalYearEndDate.getFullYear() - 1);
      issueDate = issueDateObj.toISOString().split('T')[0];
    } else {
      issueDate = new Date().toISOString().split('T')[0];
    }

    return {
      issueDate,
      taxYear: quickBondDataForDetails.startingTaxYear,
      durationYears: quickBondDataForDetails.term,
      targetPrincipal: quickBondDataForDetails.targetAmount,
    };
  };

  // ===========================================
  // Render
  // ===========================================
  return (
    <PageLayoutGrid
      leftSidebar={
        bonds.length > 0 ? (
          <CandidatesSidebar
            candidates={candidates}
            selectedCandidateId={selectedCandidateId}
            onCandidateSelect={handleCandidateSelect}
            configurationId={selectedConfigurationId}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
            showFavoritesView={showFavoritesView}
            onToggleFavoritesView={handleToggleFavoritesView}
            allConfigurations={configurations}
            onFavoriteSelect={handleFavoriteSelect}
            isSuperAdmin={isSuperAdmin}
            failedCandidates={currentConfiguration?.failedCandidates}
          />
        ) : null
      }
      rightSidebar={
        <BondsSidebar
          bonds={bonds}
          bondResults={selectedCandidate?.results?.bondSeriesResults}
          selectedBondId={selectedBondId}
          onBondSelect={handleBondSelect}
          onAddBond={() => setIsAddBondModalOpen(true)}
          onRemoveBond={workflow.deleteBond}
        />
      }
      bottomDrawer={
        selectedCandidate && project && currentConfiguration ? (
          <CandidateDataDrawer
            state={drawerState}
            candidate={selectedCandidate}
            project={project}
            configuration={currentConfiguration}
            onToggleState={setDrawerState}
            onCustomizeTaxableValues={handleCustomizeTaxableValues}
            onCustomizeMillageRates={handleCustomizeMillageRates}
          />
        ) : null
      }
    >
      {/* Main Content Area */}
      <Box width="100%" p={LAYOUT_SPACING.PAGE_PADDING} minHeight="100vh">
        {/* Configuration Cards */}
        {configurations && configurations.length > 0 ? (
          <ConfigurationCardsContainer
            configurations={configurations}
            selectedConfigurationId={selectedConfigurationId}
            onConfigurationSelect={handleConfigurationSelectWithProcessing}
            onConfigurationNameEdit={handleConfigurationNameEdit}
            onConfigurationDelete={handleConfigurationDeleteRequest}
          />
        ) : (
          <Box
            p={6}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            mb={4}
            textAlign="center"
          >
            <Text fontSize="md" color="gray.600">
              Add a bond to create your first configuration
            </Text>
          </Box>
        )}

        {/* Main Content */}
        {bonds.length === 0 ? (
          <VStack
            align="center"
            justify="center"
            minHeight="400px"
            gap={LAYOUT_SPACING.CONTENT_GAP}
          >
            <Heading size="lg" color={isReadyForBond ? 'gray.700' : 'gray.500'}>
              Add a bond to begin
            </Heading>
            <Text color="gray.400" textAlign="center" maxWidth="500px" mb={4}>
              {isReadyForBond
                ? 'Your first bond will create a configuration and generate candidates'
                : 'Complete the required information below before adding bonds'}
            </Text>
            <ReadinessChecklist
              hasProjectDates={hasProjectDates}
              hasTaxableValue={hasTaxableValue}
              hasProjectTargets={hasProjectTargets}
            />
          </VStack>
        ) : isProcessingConfig ? (
          <VStack
            align="center"
            justify="center"
            minHeight="400px"
            gap={LAYOUT_SPACING.CONTENT_GAP}
          >
            <Spinner size="xl" color="blue.500" borderWidth="3px" />
            <Text color="gray.500" fontSize="lg">
              Loading configuration...
            </Text>
          </VStack>
        ) : selectedCandidate ? (
          <VStack align="stretch" gap={LAYOUT_SPACING.SECTION_GAP}>
            {currentConfiguration && (
              <CandidateHeader
                candidate={selectedCandidate}
                configuration={currentConfiguration}
                millageInputs={currentConfiguration.millageInputs}
                onRunConfiguration={workflow.reprocessWithMillage}
              />
            )}
            <CandidateGraphView candidate={selectedCandidate} />
          </VStack>
        ) : (
          <VStack
            align="center"
            justify="center"
            minHeight="400px"
            gap={LAYOUT_SPACING.CONTENT_GAP}
          >
            <Heading size="lg" color="gray.700">
              Select a candidate to view details
            </Heading>
            <Text color="gray.400" textAlign="center" maxWidth="500px">
              Choose a candidate from the sidebar to see its financial metrics and charts
            </Text>
          </VStack>
        )}
      </Box>

      {/* Modals */}
      <AddBondModal
        isOpen={isAddBondModalOpen}
        onClose={() => setIsAddBondModalOpen(false)}
        onAddBond={handleQuickAddBond}
        onOpenDetails={handleOpenBondDetails}
        project={project}
      />

      {isBondDetailsModalOpen && quickBondDataForDetails && (
        <BondConfigurationModal
          isOpen={isBondDetailsModalOpen}
          isEditing={!!selectedBondId}
          onClose={() => {
            setIsBondDetailsModalOpen(false);
            setQuickBondDataForDetails(null);
            setSelectedBondId(null);
          }}
          onSubmit={(config) => {
            handleAddBond(config);
            setIsBondDetailsModalOpen(false);
            setQuickBondDataForDetails(null);
            setSelectedBondId(null);
          }}
          project={project}
          initialConfig={getBondModalInitialConfig()}
        />
      )}

      {/* Config Delete Confirmation */}
      <ConfirmModal
        isOpen={isConfigDeleteModalOpen}
        onClose={() => {
          setIsConfigDeleteModalOpen(false);
          setConfigToDelete(null);
          setConfigDeleteWarning(null);
        }}
        onConfirm={handleConfigurationDeleteConfirm}
        title="Delete Configuration"
        message={
          configDeleteWarning
            ? `Are you sure you want to delete this configuration? ${configDeleteWarning}`
            : 'Are you sure you want to delete this configuration?'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Cashflow Sanity Check Modal */}
      <CashflowValidationModal
        isOpen={isSanityCheckModalOpen}
        onClose={() => {
          setIsSanityCheckModalOpen(false);
          setSanityCheckResult(null);
        }}
        result={sanityCheckResult}
        candidateName={selectedCandidate?.millageInformation?.millageShape?.shapeId}
      />
    </PageLayoutGrid>
  );
};

export default ProposalDevelopmentPage;
